import React, { useState, useEffect, useContext } from "react";

import { useTranslation } from "react-i18next";
import Select from "react-select";

import MainHeader from "../../components/Headers/MainHeader";
import VerticalRadioButtons from "../../components/RadioButtons/VerticalRadioButtons";
import UserContext from "../../lib/UserContext";
import { ANSWERS } from "../../utils/constants";
import SharesSchema from "../../schemas/shares.schema";
import {
    getReactSelectStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import StepsNavButtons from "../../components/Buttons/StepsNavButtons";
import SimpleNotification from "../../components/Notifications/SimpleNotification";
import ValidationError from "../../components/Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";

const RevenueShareForm = () => {
    const [ownerName, setOwnerName] = useState("");

    //Validation States
    const [error, setError] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [showSummationError, setShowSummationError] = useState(false);

    const [sharesBody, setSharesBody] = useState({
        neospectraShare: 0,
        partnerShare: 0,
        isReseller: "",
        reseller: "",
        resellerShare: 0,
    });

    const { t } = useTranslation();
    const context = useContext(UserContext);

    const resellerOptions = [
        {
            name: "Yes",
            label: t("yes"),
            value: "Yes",
        },
        {
            name: "No",
            label: t("no"),
            value: "No",
        },
    ];

    useEffect(() => {
        setOwnerName(context.getUser()?.organization?.name);
    }, []);

    useEffect(() => {
        localValidation();
    }, [sharesBody]);

    const onChangeHandler = (e) => {
        setSharesBody((prevShares) => {
            return { ...prevShares, [e.target.name]: e.target.value };
        });
    };

    const onSelectChangeHandler = (newValue, action) => {
        setSharesBody((prevShares) => {
            return { ...prevShares, [action.name]: newValue };
        });
    };

    const handleBooleanRadioChange = (e, trueMatch) => {
        setSharesBody((prevShares) => {
            return {
                ...prevShares,
                [e.target.name]: {
                    value: e.target.value,
                    booleanValue: e.target.value === trueMatch,
                },
            };
        });
    };

    const validateTotalPercentages = () => {
        let sumPercentages = sharesBody.isReseller.booleanValue
            ? Number(sharesBody.neospectraShare) +
              Number(sharesBody.partnerShare) +
              Number(sharesBody.resellerShare)
            : Number(sharesBody.neospectraShare) +
              Number(sharesBody.partnerShare);
        if (sumPercentages !== 100) {
            setShowSummationError(true);
            setIsFormValid(false);
        } else {
            setShowSummationError(false);
            setIsFormValid(true);
        }
    };

    const localValidation = () => {
        validate(
            sharesBody,
            SharesSchema.SharesForm,
            setIsFormValid,
            setError,
            {
                allowUnknown: false,
                context: {
                    isReseller: sharesBody.isReseller.booleanValue,
                },
            }
        );

        validateTotalPercentages();
    };

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                if (!field.hide) {
                    const inputGroup = field.element();
                    return (
                        <div className="tags input-group" key={index}>
                            <h4 className="uppercase text-xs required">
                                {inputGroup.label}
                            </h4>

                            {inputGroup.input}
                        </div>
                    );
                }
            })
        );
    };

    const submitShares = () => {
        setIsFormSubmitted(true);

        if (!isFormValid) return;
    };

    const fields = [
        {
            element: () => {
                return {
                    label: t("share", {
                        shareHolder: "neospectra",
                        unit: "(%)",
                    }),
                    input: (
                        <div
                            className={`input-group ${
                                error.neospectraShare && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <input
                                type="number"
                                name="neospectraShare"
                                className="input-element"
                                placeholder={t("percentagePlaceholder", {
                                    value: "40",
                                })}
                                value={sharesBody.neospectraShare || ""}
                                onChange={onChangeHandler}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.neospectraShare}
                                />
                            )}
                        </div>
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    label: t("share", { shareHolder: ownerName, unit: "(%)" }),
                    input: (
                        <div
                            className={`input-group ${
                                error.partnerShare && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <input
                                type="number"
                                name="partnerShare"
                                className="input-element"
                                placeholder={t("percentagePlaceholder", {
                                    value: "40",
                                })}
                                value={sharesBody.partnerShare || ""}
                                onChange={onChangeHandler}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.partnerShare}
                                />
                            )}
                        </div>
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    label: t("resellerQuestion"),
                    input: (
                        <div
                            className={`input-group ${
                                error.isReseller && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <VerticalRadioButtons
                                name="isReseller"
                                options={resellerOptions}
                                onChangeHandler={(e) => {
                                    handleBooleanRadioChange(e, ANSWERS.yes);
                                }}
                                checkedReference={sharesBody.isReseller.value}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.isReseller}
                                />
                            )}
                        </div>
                    ),
                };
            },
        },
        {
            hide:
                !sharesBody.isReseller.booleanValue ||
                sharesBody.isReseller === "",
            element: () => {
                return {
                    label: t("reseller"),
                    input: (
                        <div
                            className={`input-group ${
                                error.reseller && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <Select
                                options={[
                                    { label: "1", value: "1" },
                                    { label: "2", value: "2" },
                                    { label: "3", value: "3" },
                                ]}
                                placeholder={t("selectPlaceholder", {
                                    entity: "Reseller",
                                })}
                                name="reseller"
                                value={sharesBody.reseller || ""}
                                onChange={onSelectChangeHandler}
                                styles={getReactSelectStyling({
                                    showError:
                                        error.reseller && isFormSubmitted,
                                })}
                                theme={getReactSelectTheme}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.reseller}
                                />
                            )}
                        </div>
                    ),
                };
            },
        },
        {
            hide:
                !sharesBody.isReseller.booleanValue ||
                sharesBody.isReseller === "",
            element: () => {
                return {
                    label: t("share", {
                        shareHolder: "Reseller Name ",
                        unit: "(%)",
                    }),
                    input: (
                        <div
                            className={`input-group ${
                                error.resellerShare && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <input
                                type="number"
                                name="resellerShare"
                                className="input-element"
                                placeholder={t("percentagePlaceholder", {
                                    value: "20",
                                })}
                                value={sharesBody.resellerShare || ""}
                                onChange={onChangeHandler}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.resellerShare}
                                />
                            )}
                        </div>
                    ),
                };
            },
        },
    ];

    return (
        <>
            <MainHeader header={t("revenueShare")} />
            <div className="card-table shadow flex flex-col mb-3">
                {renderFields()}
                {showSummationError && (
                    <SimpleNotification
                        message={"Summation of percentages must equal 100%"}
                    />
                )}
            </div>
            <StepsNavButtons
                next={{
                    action: submitShares,
                    disabled: isFormSubmitted && !isFormValid,
                    label: t("submit"),
                }}
            />
        </>
    );
};

export default RevenueShareForm;
